import { useState } from 'react'
import useSnackBarContext from '@contexts/snackBarContext'
import { Link, useSearchParams } from 'react-router-dom'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import ScreenContainer from './ScreenContainer'
import snakecaseKeys from 'snakecase-keys'
import { EyeMaskEndAdornment } from '@elements/InputAdornments'
import { Alert, Button, ButtonBase, } from '@mui/material'
import { PiArrowRightBold } from 'react-icons/pi'
import { FormTextField } from '@elements/Inputs'


const StyledAlert = ({ kind, children, AlertStyles }) => {
  const styles = {
    success: {
      backgroundColor: '#0C130D',
      color: '#CCE8CD',
    },
    info: {
      backgroundColor: '#071318',
      color: '#B8E7FB'
    },
    warning: {
      backgroundColor: '#191207',
      color: '#FFE2B7'
    },
    error: {
      backgroundColor: '#160B0B',
      color: '#F4C7C7'
    },
  }

  return (
    <Alert
      // variant="outlined"
      severity={kind}
      // sx={{ width: '100%' }}
      style={{
        width: '100%',
        height: size(8),
        borderRadius: 10,
        fontSize: fontSize('md'),
        marginTop: size(2),
        display: 'flex',
        alignItems: 'center',
        'MuiAlert-action': { padding: 0 },
        ...(styles[kind] || styles['success']),
        ...AlertStyles
      }}
    >
      {children}
    </Alert>
  )
}

const PasswordReset = () => {
  const { confirmPasswordReset } = useAuthContext()
  const { addSnackBar } = useSnackBarContext()
  const [searchParams] = useSearchParams()
  const passwordResetToken = searchParams.get('token')
  const [isPasswordResetSuccessful, setIsPasswordResetSuccessful] = useState(false)
  const [isPasswordMasked, setIsPasswordMasked] = useState(true)
  const [isPasswordConfMasked, setIsPasswordConfMasked] = useState(true)
  const [isTokenValid, setIsTokenValid] = useState(true)
  const [formState, setFormState] = useState({
    password: '',
    passwordConfirmation: '',
  })
  const [formErrors, setFormErrors] = useState({})
  const isPasswordMatching = 
    formState.password &&
    formState.passwordConfirmation &&
    formState.password === formState.passwordConfirmation

  const handleConfirmPasswordReset = (e) => {
    e.preventDefault()
    const data = snakecaseKeys({ ...formState, passwordResetToken, })
    confirmPasswordReset(data)
      .then(() => {
        setIsPasswordResetSuccessful(true)
        addSnackBar({ message: 'Password reset successfully' })
      })
      .catch((err) => {
        if (err?.response?.status === 401) setIsTokenValid(false)
        setFormErrors(err?.response?.data?.errors || {})
      })
  }

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value })
  }
  
  if (isPasswordResetSuccessful) return (
    <ScreenContainer style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center',  }}>
      <h1>Password Reset Successful!</h1>
      <ButtonBase
        style={{
          backgroundColor: colors.statusColors.right_now('primary'),
          padding: `${size(2.5)} ${size(3)}`,
          margin: size(3),
          maxWidth: size(70),
          borderRadius: '100px'
        }}
        component={Link}
        to='/landing'>
        Go to Mandrake Landing Page&nbsp;
        <PiArrowRightBold />
      </ButtonBase>
      {/* Spacer component, pushes the content up slightly but tracks to the center */}
      <div style={{ height: size(20), width: '100%' }} />
    </ScreenContainer>
  ); else return (
    <ScreenContainer style={{ overflowY: 'scroll', alignItems: 'center' }}>
      <form
        style={{ width: '100%', padding: size(3), maxWidth: size(70) }}
        onSubmit={handleConfirmPasswordReset}>
        <h1 style={{ textAlign: 'center' }}>Password Reset</h1>
        <p style={{ textAlign: 'center' }}>Confirm your new password</p>
        {!isTokenValid &&
          <StyledAlert kind='error' AlertStyles={{ height: 'unset' }}>
            <div style={{ paddingBottom: size(1) }}><b><u>This session has expired</u></b></div>
            Please attempt a new session or try an updated email link.
          </StyledAlert>
        }
        <br />
        <FormTextField
          name='password'
          label='Password'
          error={formErrors?.password}
          helperText={formErrors?.password?.[0]}
          type={isPasswordMasked ? 'password' : 'text'}
          required
          placeholder='Your new password'
          value={formState.password}
          onChange={handleChange}
          minimum='5'
          slotProps={{ input: {
            endAdornment: (
              <EyeMaskEndAdornment
                isMasked={isPasswordMasked}
                onClick={(e) => setIsPasswordMasked(!isPasswordMasked)}
              />
            ),
          }}}
        />
        <FormTextField
          name='passwordConfirmation'
          label='Confirm Password'
          type={isPasswordConfMasked ? 'password' : 'text'}
          required
          placeholder='Type again to confirm'
          value={formState.passwordConfirmation}
          onChange={handleChange}
          minimum='5'
          slotProps={{ input: {
            endAdornment: (
              <EyeMaskEndAdornment
                onClick={() => setIsPasswordConfMasked(!isPasswordConfMasked)}
                isMasked={isPasswordConfMasked}
              />
            )
          }}}
        />
        <StyledAlert kind='info' AlertStyles={{ height: 'unset' }}>
          Password must have:
          <ul style={{ margin: '0', padding: 0, listStyle: 'inside' }}>
            <li>{'> 5 characters'}</li>
            <li>One lowercase</li>
            <li>One uppercase</li>
            <li>
              One symbol
              <br />
              {"~`!@#$%^&*\(\)\-_+=\{\}\[\]|\;:\"'<>,.\/?"}
            </li>
          </ul>
        </StyledAlert>
        <StyledAlert kind={isPasswordMatching ? 'success' : 'info'}>
          {isPasswordMatching ? 'Passwords match' : 'Passwords must match'}
        </StyledAlert>
        <br />
        <br />
        <Button
          variant='contained'
          disabled={!isPasswordMatching}
          type='submit'
          style={{
            backgroundColor: isPasswordMatching ? colors.statusColors.right_now('primary') : 'grey',
            width: '100%',
            padding: size(2),
            borderRadius: '100px'
          }}
        >
          Submit
        </Button>
      </form>
    </ScreenContainer>
  )
}

export default PasswordReset
